import { Button, Grid, MenuItem, ToggleButton, ToggleButtonGroup } from '@mui/material';
import * as React from 'react';
import { AutocompleteElement, TextFieldElement, useFormContext } from 'react-hook-form-mui';
import {
    useWebPushGenerateInternal,
    useWebPushGetCapabilities,
    useWebPushGetCapabilityList,
} from '../../../sic-lib/api/hooks/useWebPush';
import { DropdownItem } from '../../../sic-lib/types/WebPush';
import { capabilityToWebPushLinkType } from '../../../sic-lib/utils/webPushHelper';
import Loader from '../../Shared/Loader';

type Capabilities = 'pages' | 'blogposts' | 'listings' | 'homelists' | '';

interface Props {
    siteId: number;
    url: string;
}

export default function WebPushCapabilities({ siteId, url }: Props) {
    const [capabilities, setCapabilities] = React.useState<Capabilities>('');

    const { setValue, reset } = useFormContext();

    const {
        data: capabilitiesData,
        isLoading: isCapabilitiesLoading,
        isSuccess: isCapabilitiesSuccess,
    } = useWebPushGetCapabilities(siteId);

    const {
        data: capabilityListDataList,
        refetch: refetchCapabilityList,
        isLoading: isCapabilityListLoading,
        isSuccess: isCapabilityListSuccess,
    } = useWebPushGetCapabilityList(siteId, capabilities);

    const {
        data: generateData,
        refetch: refetchGenerate,
        isLoading: isGenerateLoading,
        isSuccess: isGenerateSuccess,
        isError: isGenerateError,
    } = useWebPushGenerateInternal({
        type: capabilityToWebPushLinkType(capabilities),
        siteId,
        data: url,
    });

    const handleCapabilities = (event: React.MouseEvent<HTMLElement>, newType: string) => {
        setCapabilities(newType as Capabilities);
    };

    const handleCancelList = () => {
        setCapabilities('');
    };

    React.useEffect(() => {
        refetchCapabilityList();
        setValue('url', null);
    }, [capabilities]);

    const generateWebPushForm = () => {
        refetchGenerate();
    };

    React.useEffect(() => {
        if (generateData) {
            reset({ ...generateData });
        }
    }, [generateData]);

    return (
        <Loader isLoading={isCapabilitiesLoading}>
            <Grid item>
                <ToggleButtonGroup
                    value={capabilities}
                    color="primary"
                    aria-label="button group"
                    exclusive
                    onChange={handleCapabilities}
                >
                    {capabilitiesData && capabilitiesData.hasLandingPages && (
                        <ToggleButton value="pages">Landing Pages</ToggleButton>
                    )}
                    {capabilitiesData && capabilitiesData.hasBlogs && (
                        <ToggleButton value="blogposts">Blog Posts</ToggleButton>
                    )}
                    {capabilitiesData && capabilitiesData.hasListings && (
                        <ToggleButton value="listings">Listings</ToggleButton>
                    )}
                    {capabilitiesData && capabilitiesData.hasHomelists && (
                        <ToggleButton value="homelists">Homelists</ToggleButton>
                    )}
                    {capabilities && (
                        <Button color="error" value="" onClick={handleCancelList}>
                            Cancel
                        </Button>
                    )}
                </ToggleButtonGroup>
            </Grid>
            {capabilities && (
                <Grid item>
                    {(isNaN(parseInt(url)) || !url) && (
                        <AutocompleteElement
                            name="url"
                            label="URL"
                            options={capabilityListDataList ? capabilityListDataList : []}
                            loading={isCapabilityListLoading}
                            matchId={true}
                            autocompleteProps={{
                                disableClearable: true,
                                // getOptionLabel: option => option.label,
                                isOptionEqualToValue: (option, value) =>
                                    value !== '' ? (option as DropdownItem).id === value.id : false,
                                defaultValue: null,
                                renderOption: (props, option, state) => (
                                    <Grid key={option.id}>
                                        <MenuItem
                                            onClick={() => setValue('url', option.id)}
                                            selected={state.selected}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    </Grid>
                                ),
                                // onChange: (event, value, reason, details) => {
                                //     setValue('url', value.id);
                                // },
                            }}
                            // textFieldProps={{
                            //     disabled: url ? true : false,
                            // }}
                            required
                        />
                    )}
                </Grid>
            )}
            {isNaN(parseInt(url)) && url && (
                <Grid item>
                    <TextFieldElement
                        name="url"
                        label="Generated URL"
                        fullWidth
                        disabled
                        required
                    />
                </Grid>
            )}
        </Loader>
    );
}
